const DEFAULT_SHARE_TITLE = 'The Great Divergence';
const DEFAULT_SHARE_TEXT = 'For 90,000 of the last 100,000 years, humans organized through gift economies. What can modern collaboration networks learn from Hxaro?';
const DEFAULT_SHARE_SUMMARY = 'This interactive story traces how we shifted toward market competition and what ancient cooperation models teach us about building better collaboration networks today.';
const DEFAULT_SHARE_PROMPT = 'Read it, share it, and tell me: could your collaboration network benefit from Hxaro principles?';
const DEFAULT_SHARE_IMAGE_PATH = '/og-image.png?v=20260215';
const SHARE_CARD_VERSION = 'og20260215';

const STORY_SHARE_COPY = {
  en: {
    title: 'The Great Divergence: 100,000 Years of Economic Evolution',
    text: DEFAULT_SHARE_TEXT,
    summary: DEFAULT_SHARE_SUMMARY,
    prompt: DEFAULT_SHARE_PROMPT,
    xContext: 'A sharp timeline from anthropology to modern economics.',
    linkedinLead: 'A useful lens for founders, policymakers, and community builders:',
    emailSubject: 'A must-read interactive on gift economies and market competition',
    imageUrl: DEFAULT_SHARE_IMAGE_PATH,
  },
  de: {
    title: 'Die grosse Divergenz: 100.000 Jahre Wirtschaftsentwicklung',
    text: '90.000 der letzten 100.000 Jahre organisierten sich Menschen ueber Geschenkoekonomien. Was koennen moderne Kollaborationsnetzwerke vom Hxaro lernen?',
    summary: 'Diese interaktive Story zeigt, wie wir uns in Richtung Marktwettbewerb verschoben haben und was alte Kooperationsmodelle uns heute ueber den Aufbau besserer Kollaborationsnetzwerke lehren.',
    prompt: 'Lesen, teilen und mitdiskutieren: Koennte Ihr Kollaborationsnetzwerk von Hxaro-Prinzipien profitieren?',
    xContext: 'Eine praezise Zeitleiste von der Anthropologie bis zur modernen Netzwerk-Oekonomie.',
    linkedinLead: 'Ein nuetzlicher Blickwinkel fuer Gruender:innen von Kollaborationsnetzwerken, Policy und Community-Building:',
    emailSubject: 'Lesenswerte interaktive Story zu Geschenkoekonomien und modernen Kollaborationsnetzwerken',
    imageUrl: DEFAULT_SHARE_IMAGE_PATH,
  },
  es: {
    title: 'La Gran Divergencia: 100.000 anos de evolucion economica',
    text: 'Durante 90.000 de los ultimos 100.000 anos, la humanidad se organizo mediante economias del don. Que pueden aprender las redes de colaboracion modernas del Hxaro?',
    summary: 'Esta historia interactiva recorre como pasamos hacia la competencia de mercado y que nos ensenan los modelos de cooperacion antiguos sobre la construccion de mejores redes de colaboracion hoy.',
    prompt: 'Leela, compartela y debatamos: tu red de colaboracion podria beneficiarse de los principios Hxaro?',
    xContext: 'Una linea de tiempo precisa desde la antropologia hasta la economia moderna de redes.',
    linkedinLead: 'Una perspectiva util para fundadores de redes de colaboracion, responsables publicos y creadores de comunidad:',
    emailSubject: 'Una lectura clave sobre economias del don y redes de colaboracion modernas',
    imageUrl: DEFAULT_SHARE_IMAGE_PATH,
  },
};

function normalizeLocale(locale) {
  if (typeof locale !== 'string') return 'en';
  const short = locale.toLowerCase().slice(0, 2);
  return ['en', 'de', 'es'].includes(short) ? short : 'en';
}

function encode(value) {
  return encodeURIComponent(value ?? '');
}

function cleanText(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function normalizeShareUrl(inputUrl, fallbackUrl) {
  const candidate = cleanText(inputUrl) || cleanText(fallbackUrl);
  if (!candidate) return '';

  try {
    const parsed = new URL(candidate, typeof window !== 'undefined' ? window.location.origin : undefined);
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return candidate;
  }
}

function decorateShareUrl(inputUrl) {
  const candidate = cleanText(inputUrl);
  if (!candidate) return '';

  try {
    const parsed = new URL(candidate, typeof window !== 'undefined' ? window.location.origin : undefined);
    parsed.hash = '';

    if (!parsed.searchParams.has('utm_source')) {
      parsed.searchParams.set('utm_source', 'share');
    }
    if (!parsed.searchParams.has('utm_medium')) {
      parsed.searchParams.set('utm_medium', 'social');
    }
    parsed.searchParams.set('share_card', SHARE_CARD_VERSION);

    return parsed.toString();
  } catch {
    return candidate;
  }
}

function resolveAbsoluteUrl(inputUrl, baseUrl) {
  const candidate = cleanText(inputUrl);
  if (!candidate) return '';

  try {
    const base = cleanText(baseUrl) || (typeof window !== 'undefined' ? window.location.origin : '');
    return base ? new URL(candidate, base).toString() : candidate;
  } catch {
    return candidate;
  }
}

function clampText(text, maxLength) {
  if (!text || !Number.isFinite(maxLength) || maxLength < 20 || text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 3).trimEnd()}...`;
}

function buildSharePost(payload) {
  const url = cleanText(payload.url);
  const imageUrl = cleanText(payload.imageUrl);

  return [payload.text, payload.summary, payload.prompt, url, imageUrl !== url ? imageUrl : '']
    .map(cleanText)
    .filter(Boolean)
    .join('\n\n');
}

function buildXPost(payload) {
  const lead = cleanText(payload.xText || payload.text);
  const context = cleanText(payload.xContext || payload.summary);
  const url = cleanText(payload.url);
  const imageUrl = cleanText(payload.imageUrl);
  const urls = Array.from(new Set([url, imageUrl].filter(Boolean)));
  const urlText = urls.join(' ');
  const composed = [lead, context].filter(Boolean).join(' ');
  const maxContentLength = urlText ? Math.max(60, 274 - urlText.length - 1) : 274;

  return [clampText(composed, maxContentLength), urlText].filter(Boolean).join(' ');
}

function buildLinkedInPost(payload) {
  const lead = cleanText(payload.linkedinLead) || 'A useful lens for leaders, builders, and policy thinkers:';
  const url = cleanText(payload.url);
  const imageUrl = cleanText(payload.imageUrl);

  return [lead, payload.text, payload.summary, payload.prompt, url, imageUrl !== url ? imageUrl : '']
    .map(cleanText)
    .filter(Boolean)
    .join('\n\n');
}

async function buildShareImageFile(imageUrl) {
  if (!imageUrl || typeof window === 'undefined' || typeof File === 'undefined') {
    return null;
  }

  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return null;

    const blob = await response.blob();
    if (!blob || !blob.type?.startsWith('image/')) return null;

    const extension = blob.type.includes('png')
      ? 'png'
      : blob.type.includes('jpeg')
        ? 'jpg'
        : 'img';

    return new File([blob], `great-divergence-share.${extension}`, { type: blob.type });
  } catch {
    return null;
  }
}

export function getSharePayload(overrides = {}) {
  const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
  const normalizedUrl = normalizeShareUrl(overrides.url, fallbackUrl);
  const shareUrl = decorateShareUrl(normalizedUrl || fallbackUrl);
  const imageUrl = resolveAbsoluteUrl(
    overrides.imageUrl || DEFAULT_SHARE_IMAGE_PATH,
    shareUrl || normalizedUrl || fallbackUrl
  );

  return {
    title: overrides.title || DEFAULT_SHARE_TITLE,
    text: overrides.text || DEFAULT_SHARE_TEXT,
    summary: overrides.summary || DEFAULT_SHARE_SUMMARY,
    prompt: overrides.prompt || DEFAULT_SHARE_PROMPT,
    xText: overrides.xText || '',
    xContext: overrides.xContext || '',
    linkedinLead: overrides.linkedinLead || '',
    emailSubject: overrides.emailSubject || '',
    imageUrl,
    url: shareUrl || normalizedUrl || cleanText(overrides.url) || fallbackUrl,
  };
}

export function getStorySharePayload(localeOrOverrides = 'en', maybeOverrides = {}) {
  const locale = typeof localeOrOverrides === 'string' ? normalizeLocale(localeOrOverrides) : 'en';
  const overrides = typeof localeOrOverrides === 'object' && localeOrOverrides !== null
    ? localeOrOverrides
    : maybeOverrides;

  return getSharePayload({
    ...STORY_SHARE_COPY[locale],
    ...overrides,
  });
}

export function buildPlatformShareLinks(payloadInput = {}) {
  const payload = getSharePayload(payloadInput);
  const postText = buildSharePost(payload);
  const xPostText = buildXPost(payload);
  const linkedInPostText = buildLinkedInPost(payload);
  const linkedInInlineText = linkedInPostText.replace(/\s+/g, ' ').trim();
  const imageUrl = cleanText(payload.imageUrl);
  const emailSubject = cleanText(payload.emailSubject)
    || `${cleanText(payload.title)}: from gift economies to market competition`;

  return [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      href: `https://wa.me/?text=${encode(postText)}`,
    },
    {
      key: 'x',
      label: 'X',
      href: `https://twitter.com/intent/tweet?text=${encode(xPostText)}`,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/feed/?shareActive=true&text=${encode(linkedInInlineText)}`,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encode(payload.url)}&quote=${encode(cleanText(payload.text))}${imageUrl ? `&picture=${encode(imageUrl)}` : ''}`,
    },
    {
      key: 'email',
      label: 'Email',
      href: `mailto:?subject=${encode(emailSubject)}&body=${encode(postText)}`,
    },
  ];
}

export async function copyShareUrl(payloadInput = {}) {
  const payload = getSharePayload(payloadInput);

  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return false;
  }

  await navigator.clipboard.writeText(payload.url);
  return true;
}

export async function copySharePost(payloadInput = {}) {
  const payload = getSharePayload(payloadInput);

  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return false;
  }

  await navigator.clipboard.writeText(buildSharePost(payload));
  return true;
}

export async function tryNativeShare(payloadInput = {}) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }

  const payload = getSharePayload(payloadInput);
  const richShareText = [payload.text, payload.summary, payload.prompt, payload.url]
    .map(cleanText)
    .filter(Boolean)
    .join('\n\n');
  const shareData = {
    title: payload.title,
    text: richShareText,
    url: payload.url,
  };

  if (typeof navigator.canShare === 'function') {
    const imageFile = await buildShareImageFile(payload.imageUrl);

    if (imageFile) {
      const fileShareVariants = [
        { title: payload.title, text: richShareText, files: [imageFile] },
        { text: [cleanText(payload.text), cleanText(payload.url)].filter(Boolean).join('\n\n'), files: [imageFile] },
        { files: [imageFile] },
      ];

      for (const fileShareData of fileShareVariants) {
        if (navigator.canShare(fileShareData)) {
          await navigator.share(fileShareData);
          return true;
        }
      }
    }
  }

  await navigator.share(shareData);
  return true;
}
